import { config } from "../config";

const GRAPH_BASE = "https://graph.microsoft.com/v1.0";

/** Minimal shape of a Microsoft Graph user profile. */
export interface UserProfile {
  displayName: string;
  mail: string | null;
  jobTitle: string | null;
}

/** Minimal shape of a calendar event used by the meetings list. */
export interface MeetingEvent {
  id: string;
  subject: string;
  start: string;
  end: string;
  organizer: string;
  isOnline: boolean;
  joinUrl: string | null;
  attendeeCount: number;
}

async function graphGet<T>(path: string, token: string): Promise<T> {
  const response = await fetch(`${GRAPH_BASE}${path}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new Error(`Graph request failed (${response.status}): ${detail}`);
  }
  return (await response.json()) as T;
}

/** Reads the signed-in user's basic profile (requires User.Read). */
export async function getMyProfile(token: string): Promise<UserProfile> {
  const data = await graphGet<{
    displayName: string;
    mail: string | null;
    userPrincipalName: string;
    jobTitle: string | null;
  }>("/me?$select=displayName,mail,userPrincipalName,jobTitle", token);

  return {
    displayName: data.displayName,
    mail: data.mail ?? data.userPrincipalName ?? null,
    jobTitle: data.jobTitle,
  };
}

interface GraphEvent {
  id: string;
  subject: string | null;
  start: { dateTime: string; timeZone: string };
  end: { dateTime: string; timeZone: string };
  organizer?: { emailAddress?: { name?: string } };
  isOnlineMeeting?: boolean;
  onlineMeeting?: { joinUrl?: string } | null;
  attendees?: unknown[];
}

/**
 * Reads the user's upcoming meetings for the next 7 days (requires Calendars.Read).
 * Uses calendarView so recurring meeting instances are expanded correctly.
 */
export async function getUpcomingMeetings(token: string): Promise<MeetingEvent[]> {
  const now = new Date();
  const weekLater = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  const params = new URLSearchParams({
    startDateTime: now.toISOString(),
    endDateTime: weekLater.toISOString(),
    $select: "id,subject,start,end,organizer,isOnlineMeeting,onlineMeeting,attendees",
    $orderby: "start/dateTime",
    $top: "20",
  });

  const data = await graphGet<{ value: GraphEvent[] }>(
    `/me/calendarView?${params.toString()}`,
    token,
  );

  return data.value.map((event) => ({
    id: event.id,
    subject: event.subject?.trim() || "(No subject)",
    start: event.start.dateTime,
    end: event.end.dateTime,
    organizer: event.organizer?.emailAddress?.name ?? "Unknown",
    isOnline: Boolean(event.isOnlineMeeting),
    joinUrl: event.onlineMeeting?.joinUrl ?? null,
    attendeeCount: Array.isArray(event.attendees) ? event.attendees.length : 0,
  }));
}

/** Convenience: the configured full app URL for the hand-off button. */
export const mainAppUrl = config.mainAppUrl;
